import { BarChart3Icon, CoinsIcon, FileTextIcon } from 'lucide-react';

import { UnreadableBlobList } from '@/components/artifacts/components/UnreadableBlobList';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { useGetArtifactStatsQuery } from '@/services/api/artifactService';
import { ArtifactType } from '@/services/types/artifact-digest-types';

interface ArtifactStatsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
}

const ArtifactTypeLabels: Record<ArtifactType, string> = {
  [ArtifactType.TRANSCRIPT]: 'Transcripts',
  [ArtifactType.AUDIO_CONVERSATION]: 'Audio Conversations',
  [ArtifactType.DOCUMENT]: 'Documents',
  [ArtifactType.EMAIL]: 'Emails',
  [ArtifactType.EMAIL_ATTACHMENT]: 'Email Attachments',
  [ArtifactType.INTAKE_NOTE]: 'Intake Notes',
  [ArtifactType.MESSAGE]: 'Messages',
  [ArtifactType.MESSAGE_ATTACHMENT]: 'Message Attachments',
  [ArtifactType.CONVERSATION]: 'Conversations',
  [ArtifactType.BLOB]: 'Files',
  [ArtifactType.STAFF_NOTE]: 'Staff Notes',
};

export const ArtifactStatsDialog = ({
  isOpen,
  onClose,
  matterId,
}: ArtifactStatsDialogProps) => {
  const {
    data: stats,
    isLoading,
    error,
  } = useGetArtifactStatsQuery(
    { matterId },
    {
      skip: !isOpen || !matterId,
    },
  );

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <DialogTitle>Artifact Statistics</DialogTitle>
      <DialogDescription>
        Overview of all artifacts and their processing status for this matter.
      </DialogDescription>

      <DialogBody>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">
              Loading stats...
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-destructive">
              Failed to load artifact statistics
            </div>
          </div>
        )}

        {stats && (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-background border rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <FileTextIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="text-2xl font-semibold">
                      {formatNumber(stats.total_artifacts)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Artifacts
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background border rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <BarChart3Icon className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="text-2xl font-semibold">
                      {formatNumber(stats.readable_count)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Readable Artifacts
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background border rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CoinsIcon className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="text-2xl font-semibold">
                      {formatNumber(stats.total_tokens)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Tokens
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Breakdown by Type */}
            <div>
              <h3 className="text-lg font-medium mb-4">Breakdown by Type</h3>
              <div className="space-y-2">
                {Object.entries(stats.count_by_type)
                  .filter(([_, count]) => count > 0)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <div
                      key={type}
                      className="flex justify-between items-center py-2 px-3 bg-background rounded border"
                    >
                      <span className="text-sm font-medium">
                        {ArtifactTypeLabels[type as ArtifactType] || type}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatNumber(count)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Processing Rate */}
            {stats.total_artifacts > 0 && (
              <div>
                <h3 className="text-lg font-medium mb-2">Processing Status</h3>
                <div className="bg-background border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Readable Rate</span>
                    <span className="text-sm text-muted-foreground">
                      {(
                        (stats.readable_count / stats.total_artifacts) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{
                        width: `${(stats.readable_count / stats.total_artifacts) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatNumber(stats.readable_count)} of{' '}
                    {formatNumber(stats.total_artifacts)} artifacts processed
                  </div>
                </div>
              </div>
            )}

            {/* Unreadable blobs with retrigger controls */}
            <div>
              <h3 className="text-lg font-medium mb-2">Unreadable Blobs</h3>
              <UnreadableBlobList matterId={matterId} />
            </div>
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button plain onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
